import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
interface AttestationFiltersProps {
  onFiltersChange?: (filters: {
    cloudAccount: string;
    resourceOwner: string;
    system: string;
    department: string;
  }) => void;
  data?: {
    users: any[];
    systems: any[];
    departments: any[];
    cloudAccounts: any[];
  };
  loading?: boolean;
  error?: string | null;
  // Initial selected values (optional) for deep-linking
  initialCloudAccount?: string;
  initialResourceOwner?: string;
  initialSystem?: string;
  initialDepartment?: string;
}

function AttestationFilters({
  onFiltersChange,
  data = { users: [], systems: [], departments: [], cloudAccounts: [] },
  loading = false,
  error = null,
  initialCloudAccount,
  initialResourceOwner,
  initialSystem,
  initialDepartment,
}: AttestationFiltersProps) {
  const [selectedCloudAccount, setSelectedCloudAccount] = useState('');
  const [selectedResourceOwner, setSelectedResourceOwner] = useState('');
  const [selectedSystem, setSelectedSystem] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Apply incoming initial values only when they actually change,
  // so user selections are not constantly overridden.
  useEffect(() => {
    const nextCloud = initialCloudAccount ?? '';
    const nextOwner = initialResourceOwner ?? '';
    const nextSystem = initialSystem ?? '';
    const nextDept = initialDepartment ?? '';

    const shouldUpdateCloud = nextCloud !== selectedCloudAccount;
    const shouldUpdateOwner = nextOwner !== selectedResourceOwner;
    const shouldUpdateSystem = nextSystem !== selectedSystem;
    const shouldUpdateDept = nextDept !== selectedDepartment;

    if (!shouldUpdateCloud && !shouldUpdateOwner && !shouldUpdateSystem && !shouldUpdateDept) return;

    const updatedCloud = shouldUpdateCloud ? nextCloud : selectedCloudAccount;
    const updatedOwner = shouldUpdateOwner ? nextOwner : selectedResourceOwner;
    const updatedSystem = shouldUpdateSystem ? nextSystem : selectedSystem;
    const updatedDept = shouldUpdateDept ? nextDept : selectedDepartment;

    if (shouldUpdateCloud) setSelectedCloudAccount(updatedCloud);
    if (shouldUpdateOwner) setSelectedResourceOwner(updatedOwner);
    if (shouldUpdateSystem) setSelectedSystem(updatedSystem);
    if (shouldUpdateDept) setSelectedDepartment(updatedDept);

    onFiltersChange?.({
      cloudAccount: updatedCloud,
      resourceOwner: updatedOwner,
      system: updatedSystem,
      department: updatedDept,
    });
  }, [initialCloudAccount, initialResourceOwner, initialSystem, initialDepartment]);

  const handleCloudAccountChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedCloudAccount(value);
    onFiltersChange?.({
      cloudAccount: value,
      resourceOwner: selectedResourceOwner,
      system: selectedSystem,
      department: selectedDepartment,
    });
  };

  const handleResourceOwnerChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedResourceOwner(value);
    onFiltersChange?.({
      cloudAccount: selectedCloudAccount,
      resourceOwner: value,
      system: selectedSystem,
      department: selectedDepartment,
    });
  };

  const handleSystemChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedSystem(value);
    onFiltersChange?.({
      cloudAccount: selectedCloudAccount,
      resourceOwner: selectedResourceOwner,
      system: value,
      department: selectedDepartment,
    });
  };

  const handleDepartmentChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedDepartment(value);
    onFiltersChange?.({
      cloudAccount: selectedCloudAccount,
      resourceOwner: selectedResourceOwner,
      system: selectedSystem,
      department: value,
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl
            size="small"
            fullWidth
            sx={{ minWidth: isMobile ? 'unset' : 150 }}>
            <InputLabel id="attestation-department-select-label">Department</InputLabel>
            <Select
              labelId="attestation-department-select-label"
              id="attestation-department-select"
              name="department"
              value={selectedDepartment}
              label="Department"
              onChange={handleDepartmentChange}
              disabled={loading}>
              <MenuItem value="">All</MenuItem>
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                data.departments.map((department) => (
                  <MenuItem
                    key={department.id}
                    value={department.id.toString()}>
                    {department.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl
            size="small"
            fullWidth
            sx={{ minWidth: isMobile ? 'unset' : 150 }}>
            <InputLabel id="attestation-cloudaccount-select-label">Cloud Account</InputLabel>
            <Select
              labelId="attestation-cloudaccount-select-label"
              id="attestation-cloudaccount-select"
              name="cloudAccount"
              value={selectedCloudAccount}
              label="Cloud Account"
              onChange={handleCloudAccountChange}
              disabled={loading}>
              <MenuItem value="">All</MenuItem>
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                data.cloudAccounts.map((cloudAccount) => (
                  <MenuItem
                    key={cloudAccount.id}
                    value={cloudAccount.id.toString()}>
                    {cloudAccount.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl
            size="small"
            fullWidth
            sx={{ minWidth: isMobile ? 'unset' : 150 }}>
            <InputLabel id="attestation-system-select-label">System</InputLabel>
            <Select
              labelId="attestation-system-select-label"
              id="attestation-system-select"
              name="system"
              value={selectedSystem}
              label="System"
              onChange={handleSystemChange}
              disabled={loading}>
              <MenuItem value="">All</MenuItem>
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                data.systems.map((system) => (
                  <MenuItem key={system.id} value={system.id.toString()}>
                    {system.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl
            size="small"
            fullWidth
            sx={{ minWidth: isMobile ? 'unset' : 150 }}>
            <InputLabel id="attestation-owner-select-label">Resource owner</InputLabel>
            <Select
              labelId="attestation-owner-select-label"
              id="attestation-owner-select"
              name="resourceOwner"
              value={selectedResourceOwner}
              label="Resource owner"
              onChange={handleResourceOwnerChange}
              disabled={loading}>
              <MenuItem value="">All</MenuItem>
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                data.users.map((user) => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.firstName} ({user.username})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Box
              sx={{
                color: 'error.main',
                fontSize: '0.875rem',
                mt: 1,
              }}>
              Error loading filter data: {error}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default AttestationFilters;
